'use client'

import React, { useEffect, useState } from 'react'

import { Share, Share2 } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

/**
 * Detects if the user is on an Apple platform (macOS, iOS, iPadOS)
 */
function useIsApplePlatform(): boolean | null {
  const [isApple, setIsApple] = useState<boolean | null>(null)

  useEffect(() => {
    // Check for Apple platforms
    const userAgent = navigator.userAgent.toLowerCase()
    const platform = navigator.platform?.toLowerCase() ?? ''

    const isApplePlatform =
      platform.includes('mac') ||
      platform.includes('iphone') ||
      platform.includes('ipad') ||
      platform.includes('ipod') ||
      /iphone|ipad|ipod|mac/i.test(userAgent) ||
      // Check for iOS Safari on iPad (which reports as Mac in newer versions)
      (platform.includes('mac') && navigator.maxTouchPoints > 1)

    setIsApple(isApplePlatform)
  }, [])

  return isApple
}

export interface ShareIconProps {
  className?: string
}

/**
 * Platform-aware share icon component.
 * Shows the iOS share icon (arrow up from line) on Apple platforms,
 * and the standard Share2 icon on other platforms.
 */
export const ShareIcon: React.FC<ShareIconProps> = ({ className }) => {
  const isApple = useIsApplePlatform()

  // Default to Share2 until we detect the platform to avoid hydration mismatch
  if (isApple === null) {
    return <Share2 className={twMerge('h-4 w-4', className)} />
  }

  return isApple ? (
    <Share className={twMerge('h-4 w-4', className)} />
  ) : (
    <Share2 className={twMerge('h-4 w-4', className)} />
  )
}

export default ShareIcon
